export default function AboutSection() {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            About SafePoint Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Where Law Enforcement Experience Meets Construction Expertise
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16">
          <div>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              At SafePoint Solutions, we operate at the intersection of <strong>law enforcement experience</strong>, <strong>construction expertise</strong>, and <strong>property management oversight</strong> bringing together disciplines that are rarely aligned under one trusted provider.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Our leadership and advisory team is built on more than <strong>20 years of state and federal law enforcement experience</strong> combined with hands on construction consulting and property management exposure. This unique background allows us to view properties the way others cannot: not just as structures, but as systems where safety, liability, maintenance, and operations are deeply connected.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              We understand how small deficiencies turn into serious risks. A cracked paver, poor lighting, unsecured access point, deferred maintenance item, or uncoordinated repair isn't just a construction issue it's a <strong>safety concern</strong>, a <strong>liability exposure</strong>, and often a management failure. SafePoint Solutions exists to identify those gaps early and ensure they are addressed correctly, efficiently, and with accountability.
            </p>
          </div>

          <div className="relative">
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/ae118e97ebc078ab009b24ac4999483c/7f2fe4de4d2832882670c72d1ef08b5d.png"
                alt="SafePoint Solutions Professional Team"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-2xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-slate-900 mb-6">What Sets Us Apart</h3>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            What truly sets SafePoint Solutions apart is our <strong>vetted professional network</strong>. We work alongside trusted attorneys, general contractors, engineers, inspectors, and skilled trade professionals, allowing us to coordinate solutions across virtually every aspect of a property. Whether the need is legal guidance, structural improvements, security infrastructure, preventative maintenance, or specialized trade work, we serve as a <strong>single point of contact</strong>, ensuring all efforts are aligned and executed to standard.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">20+</div>
              <div className="text-sm text-gray-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">Support Available</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">100%</div>
              <div className="text-sm text-gray-600">Vetted Network</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h3 className="text-3xl font-bold text-slate-900 mb-8">Our Services</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Property and home inspections</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Security and vulnerability assessments</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Construction and maintenance consulting</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Preventative maintenance planning</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Storm preparedness and post-event evaluations</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Property and building management oversight</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Security-driven construction considerations (lighting, fencing, access control, CPTED principles)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Coordination and oversight of repair, improvement, and special projects</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                <span className="text-gray-700">Integration with trusted vendors and professional partners</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-3xl font-bold text-slate-900 mb-8">Our Approach</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              We don't just identify problems we <strong>manage solutions</strong>. From initial assessment through coordination and follow-through, SafePoint Solutions ensures work is done right the first time, eliminating guesswork, reducing risk, and protecting your investment.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Our mission is simple: to deliver <strong>peace of mind through proactive protection</strong>. By combining law enforcement insight, construction knowledge, and a powerful professional network, SafePoint Solutions provides comprehensive property protection today and into the future.
            </p>

            <div className="bg-teal-600 text-white p-8 rounded-xl">
              <i className="ri-team-line text-4xl mb-4"></i>
              <h4 className="text-2xl font-bold mb-3">Single Point of Contact</h4>
              <p className="text-teal-100 leading-relaxed">
                We coordinate with attorneys, contractors, engineers, inspectors, and trade professionals to ensure all efforts are aligned and executed to standard.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}